<template>
    <div>
        <template v-if="serviceAggregate.isPaymentRequired()">
            <ConfirmStripePayment
                v-if="
                    serviceAggregate.integration?.provider ===
                    IntegrationProviderEnum.StripeConnect
                "
                :company="company"
                :draftMeeting="draftMeeting"
                :serviceAggregate="serviceAggregate"
                :customerPaymentMethods="customerPaymentMethods"
                :turnstileEnabled="turnstileEnabled"
                :turnstileSiteKey="turnstileSiteKey"
                :stripePublicKey="stripePublicKey"
                :hasSavePaymentMethodFeatureFlag="
                    hasSavePaymentMethodFeatureFlag
                "
                :isProcessingPayment="isProcessingPayment"
                :numberOfRecurringMeetingsToSchedule="
                    numberOfRecurringMeetingsToSchedule
                "
                :numberOfRequestedSeats="numberOfRequestedSeats"
                :localizer="localizer"
                :canScheduleMeeting="canScheduleMeeting"
                @onStart="handleConfirmationStarted"
                @onError="handleConfirmationFailed"
                @onScheduleMeetingRequested="requestScheduleMeeting"
            />
            <ConfirmPayPalPayment
                v-else-if="
                    serviceAggregate.integration?.provider ===
                    IntegrationProviderEnum.PayPalMultiParty
                "
                :company="company"
                :draftMeeting="draftMeeting"
                :serviceAggregate="serviceAggregate"
                :turnstileEnabled="turnstileEnabled"
                :turnstileSiteKey="turnstileSiteKey"
                :payPalClientId="payPalClientId"
                :isProcessingPayment="isProcessingPayment"
                :numberOfRecurringMeetingsToSchedule="
                    numberOfRecurringMeetingsToSchedule
                "
                :numberOfRequestedSeats="numberOfRequestedSeats"
                :localizer="localizer"
                :canScheduleMeeting="canScheduleMeeting"
                @onStart="handleConfirmationStarted"
                @onError="handleConfirmationFailed"
                @onScheduleMeetingRequested="requestScheduleMeeting"
            />
        </template>
        <template v-else>
            <ConfirmWithoutPayment
                :isProcessingPayment="isProcessingPayment"
                :turnstileEnabled="turnstileEnabled"
                :turnstileSiteKey="turnstileSiteKey"
                :localizer="localizer"
                :canScheduleMeeting="canScheduleMeeting"
                @onStart="handleConfirmationStarted"
                @onScheduleMeetingRequested="requestScheduleMeeting"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
    import { type PropType } from 'vue'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import CustomerPaymentMethod from '@/App/CustomerPaymentMethod/Entity/CustomerPaymentMethod'
    import Company from '@/App/Company/Entity/Company'
    import DraftMeeting from '@/App/Booking/Draft/Entity/DraftMeeting'
    import {
        type ConfirmBookMeetingCanScheduleMeetingCallback,
        type ConfirmBookMeetingOnErrorCallbackParams,
        type ConfirmBookMeetingOnScheduleMeetingRequested,
        type ConfirmBookMeetingOnStartCallbackParams,
    } from '@/Shared/AutoGenerated/Types'
    import { IntegrationProviderEnum } from '@/Shared/AutoGenerated/Main'
    import ConfirmWithoutPayment from '@/BookingForm/BookingForm/Component/Confirmation/ConfirmWithoutPayment.vue'
    import ConfirmStripePayment from '@/BookingForm/BookingForm/Component/Confirmation/ConfirmStripePayment.vue'
    import ConfirmPayPalPayment from '@/BookingForm/BookingForm/Component/Confirmation/ConfirmPayPalPayment.vue'
    import type LocalizerService from '@/Shared/Service/Localization/LocalizerService'

    const emits = defineEmits<{
        onStart: [ConfirmBookMeetingOnStartCallbackParams]
        onError: [ConfirmBookMeetingOnErrorCallbackParams]
        onScheduleMeetingRequested: [
            ConfirmBookMeetingOnScheduleMeetingRequested,
        ]
    }>()

    const props = defineProps({
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        draftMeeting: {
            type: Object as PropType<DraftMeeting>,
            required: true,
        },
        serviceAggregate: {
            type: Object as PropType<ServiceAggregate>,
            required: true,
        },
        customerPaymentMethods: {
            type: Array as PropType<CustomerPaymentMethod[]>,
            required: true,
        },
        turnstileEnabled: {
            type: Boolean,
            required: true,
        },
        turnstileSiteKey: {
            type: String,
            required: true,
        },
        stripePublicKey: {
            type: String,
            default: null,
        },
        payPalClientId: {
            type: String,
            required: true,
        },
        hasSavePaymentMethodFeatureFlag: {
            type: Boolean,
            default: null,
        },
        isProcessingPayment: {
            type: Boolean,
            required: true,
        },
        numberOfRecurringMeetingsToSchedule: {
            type: Number,
            required: true,
        },
        numberOfRequestedSeats: {
            type: Number,
            required: true,
        },
        localizer: {
            type: Object as PropType<LocalizerService>,
            required: true,
        },
        canScheduleMeeting: {
            type: Function as PropType<ConfirmBookMeetingCanScheduleMeetingCallback>,
            required: true,
        },
    })

    const handleConfirmationStarted = () => {
        emits('onStart', {})
    }

    const handleConfirmationFailed = (
        eventPayload: ConfirmBookMeetingOnErrorCallbackParams,
    ) => {
        emits('onError', eventPayload)
    }

    const requestScheduleMeeting = () => {
        emits('onScheduleMeetingRequested', {})
    }
</script>
